import {
  DownloadOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  FileProtectOutlined,
  LeftOutlined,
  MessageOutlined,
  PrinterOutlined,
  RightOutlined
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  List,
  Modal,
  Row,
  Space,
  Spin,
  Table,
  Tooltip,
  Typography,
  message
} from 'antd';
import FileSaver from 'file-saver';
import * as moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Document, Page } from 'react-pdf';
import { useHistory, useLocation } from 'react-router-dom';
import { SizeMe } from 'react-sizeme';
import { CONFIG } from '../constants';
import { Construccion } from '../forms/hogar/DescripcionesHogar';
import {
  Asegurados,
  Beneficiarios,
  CalendarioPagos
} from '../forms/personal/Descripciones';
import {
  filtrarComprobantes,
  filtrarDocumentos
} from '../functions/filtradoArchivos';
import { b64toBlob } from '../functions/images';
import {
  errorHandler,
  getCarouselData,
  getTable,
  relaciones,
  relacionesAsegurados
} from '../utils';
import { instanceRef } from '../utils/axios';
import { formatoMonedaGenerico } from '../utils/converters';
import DescripcionPlan from './DescripcionPlan';
import Image360 from './Image360';
import ResponsiveContainer from './ResponsiveContainer';

const formatosImagen = 'image/jpeg,image/heif,image/heic,image/png';

const styles = {
  list: {
    textAlign: 'left',
    maxWidth: 500
  },
  icon: { fontSize: 20 },
  textOverflow: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '200px',
    marginBlockEnd: 'auto'
  }
};

export const getDescripcionDocumento = (documento) => {
  switch (documento) {
    case 'CIA':
      return 'Cédula de Identidad (Frente)';
    case 'CID':
      return 'Cédula de Identidad (Dorso)';
    case 'CVA':
      return 'Cédula Verde (Frente)';
    case 'CVD':
      return 'Cédula Verde (Dorso)';
    case 'HAD':
      return 'Habilitación (Frente)';
    case 'HAA':
      return 'Habilitación (Dorso)';
    case 'CERV':
      return 'Certificado de venta';
    case 'CP':
      return 'Chapa provisoria';
    case 'CERN':
      return 'Certificado de nacionalización';
    case 'Declaracion':
      return 'Declaración jurada de orígen de fondos';
    case 'FormularioUnico':
      return 'Formulario de identificación del cliente';
    case 'Propuesta':
      return 'Propuesta';
    case 'Certificado':
      return 'Certificado de cobertura';
    case 'Solicitud':
      return 'Solicitud';
    default:
      return <p style={styles.textOverflow}>{documento}</p>;
  }
};

export const getDescripcionesIngreso = (ingreso) => {
  switch (ingreso) {
    case '2000000':
      return '1.000.000 a 5.000.000';
    case '6000000':
      return '5.000.000 a 10.000.000';
    case '11000000':
      return '10.000.000 a 15.000.000';
    case '16000000':
      return 'Más de 15.000.000';
    default:
      return 'N/A';
  }
};

export const DescripcionesHogar = ({ fields, construccion }) => {
  const { beneficiarios } = fields;
  return (
    <>
      <Descriptions
        title="Datos del Inmueble"
        style={{ textAlign: 'left', paddingBottom: 40 }}
        bordered
        size="small"
        column={{ md: 2, sm: 1, xs: 1 }}
      >
        <Descriptions.Item label="Departamento">
          {fields.inmueble?.departamento.descripcion}
        </Descriptions.Item>
        <Descriptions.Item label="Ciudad">
          {fields.inmueble?.distrito.descripcion}
        </Descriptions.Item>
        <Descriptions.Item label="Dirección">
          {fields.inmueble?.domicilio}
        </Descriptions.Item>
        <Descriptions.Item label="Cuenta Corriente Catastral Nro.">
          {fields.inmueble?.cuenta}
        </Descriptions.Item>
      </Descriptions>
      <Beneficiarios
        {...{
          beneficiarios: { list: beneficiarios.map((item) => ({ ...item })) },
          relaciones
        }}
      />
      {construccion && (
        <Construccion
          {...{
            construccion
          }}
        />
      )}
    </>
  );
};

export const DescripcionesPersonal = ({ fields }) => {
  const { asegurados, beneficiarios } = fields;
  const montosPorTipo = fields.plan
    ? {
        titular: Number(fields.plan.monto_total),
        conyuge: Number(fields.plan.monto_total_conyuge),
        hijo: Number(fields.plan.monto_total_hijos)
      }
    : {};
  const getMonto = (record) => {
    if (!record) {
      return '';
    }
    const { tipo } = record;
    return tipo ? montosPorTipo[tipo] : '';
  };
  return (
    <>
      <Asegurados
        {...{
          asegurados: {
            list: asegurados.map((item) => ({ ...item, monto: getMonto(item) }))
          },
          relacionesAsegurados,
          listaVigenciaPago: fields.cotizacion.aseguradoraEmpresaProducto.ePV.map(
            ({ vP }) => vP
          ),
          vigenciaPago: fields.id_vigencia_pago
        }}
      />

      <Beneficiarios
        {...{
          beneficiarios: { list: beneficiarios.map((item) => ({ ...item })) },
          relaciones
        }}
      />
    </>
  );
};

export const DescripcionesAsegurado = ({ solicitante }) => {
  return (
    <Descriptions
      title="Datos del Asegurado"
      style={{ textAlign: 'left', paddingBottom: 40 }}
      bordered
      size="small"
      column={{ md: 2, sm: 1, xs: 1 }}
    >
      <Descriptions.Item label="Nombre y Apellido" span={2}>
        {solicitante?.persona?.nombres}
      </Descriptions.Item>
      <Descriptions.Item label="Cédula de Identidad">
        {solicitante?.persona?.cedula}
      </Descriptions.Item>
      <Descriptions.Item label="Domicilio">
        {solicitante?.persona?.domicilio}
      </Descriptions.Item>
      <Descriptions.Item label="Contacto">
        {solicitante?.persona?.celular}
      </Descriptions.Item>
      <Descriptions.Item label="Correo">
        {solicitante?.persona?.correo}
      </Descriptions.Item>
      <Descriptions.Item label="Departamento">
        {solicitante?.persona?.departamento?.descripcion || 'N/A'}
      </Descriptions.Item>
      <Descriptions.Item label="Ciudad">
        {solicitante?.persona?.distrito?.descripcion || 'N/A'}
      </Descriptions.Item>
      <Descriptions.Item label="Nacionalidad">
        {solicitante?.persona?.nacionalidad}
      </Descriptions.Item>
      <Descriptions.Item label="Sexo">
        {solicitante?.persona?.sexo}
      </Descriptions.Item>
      <Descriptions.Item label="Estado civil">
        {solicitante?.persona?.estado_civil}
      </Descriptions.Item>
      <Descriptions.Item label="Lugar nacimiento">
        {solicitante?.persona?.lugar_nacimiento}
      </Descriptions.Item>
      <Descriptions.Item label="Fecha nacimiento">
        {solicitante?.persona?.fecha_nacimiento &&
          moment(solicitante?.persona?.fecha_nacimiento, 'YYYY-MM-DD').format(
            'DD/MM/YYYY'
          )}
      </Descriptions.Item>
    </Descriptions>
  );
};

export const DescripcionesVehiculo = ({ fields }) => {
  return (
    <Descriptions
      title="Datos del Vehículo"
      style={{ textAlign: 'left', paddingBottom: 40 }}
      bordered
      size="small"
      column={{ md: 2, sm: 1, xs: 1 }}
    >
      <Descriptions.Item label="Marca">
        {fields.cotizacion?.vehiculo?.marca?.descripcion}
      </Descriptions.Item>
      <Descriptions.Item label="Modelo">
        {fields.cotizacion?.vehiculo?.modelo?.descripcion}
      </Descriptions.Item>
      <Descriptions.Item label="Submodelo">
        {fields.cotizacion?.vehiculo?.submodelo?.descripcion}
      </Descriptions.Item>
      <Descriptions.Item label="Año">
        {fields.cotizacion?.vehiculo?.aniosubmodelo?.descripcion}
      </Descriptions.Item>
      <Descriptions.Item label="Monto">
        {formatoMonedaGenerico(
          fields.moneda,
          fields.cotizacion?.vehiculo?.monto_asegurado
        )}
      </Descriptions.Item>
      <Descriptions.Item label="Chassis">
        {fields.cotizacion?.vehiculo?.chassis}
      </Descriptions.Item>
      <Descriptions.Item label="Chapa">
        {fields.cotizacion?.vehiculo?.chapa}
      </Descriptions.Item>
      <Descriptions.Item label="Cant. ocupantes">
        {fields.cotizacion?.vehiculo?.submodelo?.plazas}
      </Descriptions.Item>
    </Descriptions>
  );
};

export const DatosProducto = ({ fields, construccionData }) => {
  switch (fields.cotizacion.id_producto) {
    case 1:
      return <DescripcionesVehiculo fields={fields} />;
    case 2:
      return <DescripcionesPersonal fields={fields} />;
    case 3:
      return <DescripcionesPersonal fields={fields} />;
    case 4:
      return (
        <DescripcionesHogar fields={fields} construccion={construccionData} />
      );
    case 5:
      return (
        <DescripcionesHogar fields={fields} construccion={construccionData} />
      );
    case 9:
      return <DescripcionesVehiculo fields={fields} />;
    default:
      return null;
  }
};

const Vigencia = ({ fields, tipo }) => {
  const fechaIni =
    fields?.json_plan?.fechaInicioVigencia ||
    fields?.poliza?.fecha_inicio_vigencia;
  const fechaFin =
    fields?.json_plan?.fechaFinVigencia || fields?.poliza?.fecha_fin_vigencia;
  let dias = null;
  if (fields?.json_plan?.cantidadDias) {
    dias = fields?.json_plan?.cantidadDias;
  } else {
    dias = moment(fechaFin)
      .startOf('day')
      .diff(moment(fechaIni).startOf('day'), 'days');
  }
  const vigenciaPago = {
    desde: fechaIni ? moment(fechaIni) : null,
    hasta: fechaFin ? moment(fechaFin) : null,
    cantidad_dias: dias
  };
  const columns = [
    {
      title: 'Vigencia',
      dataIndex: 'nombre',
      key: 'nombre',
      colSpan: 2,
      align: 'left',
      render: (text, row, index) => {
        if (index > 0) {
          return text;
        }
        return {
          children: text,
          props: {
            colSpan: 2
          }
        };
      }
    },
    {
      title: 'Valor',
      dataIndex: 'valor',
      key: 'valor',
      colSpan: 0,
      render: (value, row, index) => {
        const obj = {
          children: value,
          props: {}
        };
        if (index === 0) {
          obj.props.colSpan = 0;
        }
        return obj;
      }
    }
  ];

  const data = [
    {
      nombre:
        tipo === 'vehiculo'
          ? '* El inicio de la vigencia está sujeto al pago'
          : '* La vigencia será renovada por el mismo periodo recurrentemente, sujeto al pago',
      key: '0',
      valor: ''
    },
    {
      nombre: 'Desde',
      key: '1',
      valor: vigenciaPago.desde.format('DD/MM/YYYY') || ''
    },
    {
      nombre: 'Hasta',
      key: '2',
      valor: vigenciaPago.hasta.format('DD/MM/YYYY') || ''
    },
    {
      nombre: 'Días',
      key: '3',
      valor: vigenciaPago.cantidad_dias
    }
  ];

  return (
    <>
      <Descriptions
        title="Datos de Vigencia"
        style={{ textAlign: 'left', marginTop: 30 }}
      >
        <Descriptions.Item label="">
          <Table
            fixed
            scroll={false}
            style={{ width: '100%' }}
            bordered
            dataSource={data}
            columns={columns}
            pagination={false}
          />
        </Descriptions.Item>
      </Descriptions>
    </>
  );
};

function SolicitudView(props) {
  const [fields, setFields] = useState();
  const [documentos, setDocumentos] = useState([]);
  const [formularios, setFormularios] = useState([]);
  const [comprobantesIngreso, setComprobantesIngreso] = useState([]);
  const [facturaServicios, setFacturaServicios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [solicitante, setSolicitante] = useState();
  const [errorAVerificar, setErrorAVerificar] = useState();
  const [numPages, setNumPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [dialogConfig, setDialogConfig] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const [fileBase64, setFileBase64] = useState();
  const [imageVisible, setImageVisible] = useState(false);
  const [construccionData, setConstruccionData] = useState();

  const { executeRecaptcha } = useGoogleReCaptcha();

  function onDocumentLoadSuccess({ numPages: nextNumPages }) {
    setNumPages(nextNumPages);
  }

  const nextPage = () => {
    if (currentPage < numPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const previousPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const downloadFile = (file, name) => {
    const blob = b64toBlob(file, 'application/pdf');
    FileSaver.saveAs(blob, name);
  };

  const fetch = async () => {
    const fullPath = location.pathname;
    const tokenAVerificar = props.match.params.id;

    try {
      const response = await instanceRef.instance.get(
        `/solicitud/${tokenAVerificar}?fullPath=${fullPath}`
      );
      setFields(response.data.data);
      if (response.data.data.solicitante) {
        setSolicitante(JSON.parse(response.data.data.solicitante));
        const datosVerificarPendiente = response.data.data.manejoErrores?.find(
          (error) => error.estado === 'PENDIENTE'
        );
        setErrorAVerificar(datosVerificarPendiente);
      }
      setLoading(true);

      const responseDocumentos = await instanceRef.instance.get(
        `/solicitud/files/${response.data.data.token}`
      );
      const baseURL = CONFIG.URL_BASE;
      const url = `${baseURL}/form/certificado?token=${response.data.data.token}`;
      const archivos = responseDocumentos.data.data.map((item) => ({
        ...item,
        url: `${baseURL}/solicitud/file/${response.data.data.token}?id=${item.id}`
      }));
      const certificado = {
        tipo: 'Certificado.pdf',
        url
      };

      const docs = filtrarDocumentos(archivos);

      // Comprobantes de ingreso
      const comprobantes = filtrarComprobantes(archivos, 'comprobanteingreso');
      const facturas = filtrarComprobantes(archivos, 'facturaservicios');

      setComprobantesIngreso(comprobantes);
      setFacturaServicios(facturas);
      if (response.data.data && response.data.data.estado === 'APROBADO') {
        setDocumentos([...docs, certificado]);
      } else {
        setDocumentos(docs);
      }
      if (response.data.data.estado === 'PENDIENTE_AUTORIZACION') {
        const files = await instanceRef.instance.post(
          `form/documentos?token=${tokenAVerificar}&autorizacion=true`
        );

        if (files.data.data && files.data.data.length === 0) {
          message.error('No se han encontrado los documentos');
          setLoading(false);
        } else {
          setFormularios(
            files.data.data
              .map(({ filename, content }) => ({
                tipo: filename,
                content
              }))
              .filter(
                ({ tipo }) =>
                  tipo === 'Propuesta.pdf' || tipo === 'Formulario.pdf'
              )
          );
          setLoading(false);
        }
      }
    } catch (error) {
      errorHandler(error);
      if (error.response && error.response.status === 400) {
        history.push(
          `/regenerar-token?flujo=solicitud-firmada&tokenExpirado=${tokenAVerificar}`
        );
      }
    }
    setLoading(false);
  };

  const fetchConstruccion = useCallback(async () => {
    setLoading(true);
    try {
      const response = await instanceRef.instance.post(
        `/construccion/detalle/inmueble`,
        {
          idInmueble: fields?.inmueble?.id
        }
      );
      setConstruccionData(response.data.data);
    } catch (error) {
      errorHandler(error);
    }
    setLoading(false);
  }, [fields?.inmueble?.id]);

  const sendSms = async () => {
    const { cotizacion } = fields;
    try {
      const reCaptchaToken = await executeRecaptcha('sms/firmar');
      const data = {
        reCaptchaToken,
        persona: {
          celular: cotizacion.persona.celular
        },
        token: fields.token,
        idEmpresa: cotizacion.aseguradoraEmpresa.id_empresa
      };
      message.info(
        `${cotizacion.persona.celular} recibirá un mensaje de texto para firmar el documento`
      );
      await instanceRef.instance.post(`sms/firmar`, data);
    } catch (error) {
      // error
    }
  };

  const getFile = async (item, descargarArchivo) => {
    try {
      const data = {
        token: fields.token,
        id: item.id,
        tipo: item.tipo
      };

      const response = await instanceRef.instance.post(
        `/solicitud/fileV2`,
        data
      );

      // Se recibe un archivo base64
      if (response.data) {
        setFileBase64(response.data.fileBase64);

        if (item.tipo.toString().includes('pdf')) {
          const dataPdf = {
            open: true,
            content: response.data.fileBase64,
            title: ''
          };
          if (descargarArchivo) {
            const parts = dataPdf.content.split(',');
            // get data
            const base64Data = parts[1];
            downloadFile(base64Data, item.tipo);
          } else {
            setCurrentPage(0);
            setDialogConfig(dataPdf);
          }
        } else {
          setImageVisible(true);
        }
      }
    } catch (error) {
      //  console.log('error: ', error);
    }
  };

  const handleOpenUrl = (url) => {
    history.push(url);
  };

  useEffect(() => {
    if (fields && fields.inmueble) {
      fetchConstruccion();
    }
  }, [fields, fetchConstruccion]);

  useEffect(() => {
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ListDocumentos = ({ title, dataSource }) => {
    return (
      <List
        header={<Typography.Title level={5}>{title}</Typography.Title>}
        dataSource={dataSource}
        loading={loading}
        size="large"
        bordered
        style={styles.list}
        renderItem={(item) => {
          const partes = item.tipo.split('.');
          const tipo = partes[0];
          const extension = partes.length > 0 ? partes[1] : undefined;

          return (
            <>
              <List.Item
                actions={
                  item.url
                    ? [
                        <Tooltip title="Descargar">
                          <Button
                            type="text"
                            icon={<DownloadOutlined />}
                            onClick={() => getFile(item, true)}
                            size="middle"
                            hidden={!item.tipo.includes('pdf')}
                          />
                        </Tooltip>,
                        <Button
                          type="link"
                          icon={
                            formatosImagen
                              .split(',')
                              .map((formato) => formato.split('/')[1])
                              .includes(extension) ? (
                              <FileImageOutlined />
                            ) : (
                              <FilePdfOutlined />
                            )
                          }
                          onClick={() => getFile(item)}
                          target="blank"
                        >
                          ver
                        </Button>
                      ]
                    : [
                        <Tooltip title="Descargar">
                          <Button
                            type="text"
                            icon={<DownloadOutlined />}
                            onClick={() =>
                              downloadFile(item.content, item.tipo)
                            }
                            size="middle"
                          />
                        </Tooltip>,
                        <Button
                          type="link"
                          icon={
                            formatosImagen
                              .split(',')
                              .map((formato) => formato.split('/')[1])
                              .includes(extension) ? (
                              <FileImageOutlined />
                            ) : (
                              <FilePdfOutlined />
                            )
                          }
                          onClick={() => {
                            setCurrentPage(0);
                            setDialogConfig({
                              open: true,
                              content: `data:application/pdf;base64,${item.content}`,
                              title: '',
                              extension
                            });
                          }}
                        >
                          ver
                        </Button>
                      ]
                }
              >
                <List.Item.Meta title={getDescripcionDocumento(tipo)} />
              </List.Item>
            </>
          );
        }}
      />
    );
  };

  return (
    <ResponsiveContainer size="large">
      <Card
        title={`Solicitud Nro.: ${fields?.id || ''}`}
        style={{ textAlign: 'left' }}
        extra={
          fields?.estado === 'PENDIENTE' && (
            <Tooltip
              title={() =>
                `Enviar por ${
                  fields.cotizacion?.aseguradoraEmpresa?.empresa
                    ?.tiene_whatsapp &&
                  solicitante?.persona?.via_contacto === 'whatsapp'
                    ? 'Whatsapp'
                    : 'SMS'
                } el enlace para firmar`
              }
            >
              <Button
                type="text"
                shape="circle"
                onClick={sendSms}
                icon={<MessageOutlined />}
                size="middle"
                disabled={fields.cotizacion?.id_producto === 9}
              />
            </Tooltip>
          )
        }
      >
        {fields && (
          <>
            <div style={{ marginBottom: '10px' }}>
              {fields.tiene_impresion ? (
                <Space>
                  <PrinterOutlined /> Póliza para impresión
                </Space>
              ) : (
                ''
              )}
            </div>
            {fields.poliza &&
            (fields.poliza.estado === 'APROBADO' ||
              fields.poliza.estado === 'VIGENTE') ? (
              <>
                <Button
                  type="link"
                  icon={<FileProtectOutlined />}
                  size="middle"
                  onClick={() =>
                    handleOpenUrl(`/polizas/${fields.poliza.token}`)
                  }
                >
                  Póliza
                </Button>
                <br />
                <br />
              </>
            ) : (
              <span>
                {(fields.estado === 'PROCESANDO' ||
                  //  fields.estado === 'PENDIENTE_DOCUMENTOS' ||
                  // fields.estado === 'VERIFICAR' ||
                  fields.estado === 'FINALIZADO') && (
                  <>
                    <Alert
                      message="¡Estamos procesando su solicitud de póliza!. Le estaremos notificando cuando continúe el proceso de emisión de póliza."
                      type="info"
                      banner
                    />
                    <br />
                    <br />
                  </>
                )}
                {
                  // fields.estado === 'PENDIENTE_DOCUMENTOS' ||
                  fields.estado === 'PENDIENTE' && (
                    <>
                      <Alert
                        message="La solicitud se encuentra a la espera de la firma por parte del cliente."
                        type="warning"
                        banner
                      />
                      <br />
                      <br />
                    </>
                  )
                }
                {fields.estado === 'VERIFICAR' &&
                  (!errorAVerificar ||
                  errorAVerificar.proceso !== 'INSPECCION' ? (
                    <>
                      <Alert
                        message="La solicitud se encuentra en proceso de verificación. Una vez que sea aprobada, le estaremos notificando nuevamente."
                        type="warning"
                        banner
                      />
                      <br />
                      <br />
                    </>
                  ) : (
                    <>
                      <Alert
                        message="La solicitud se encuentra a la espera de la inspección del vehículo"
                        type="warning"
                        banner
                      />
                      <br />
                      <br />
                    </>
                  ))}

                {fields.estado === 'PENDIENTE_AUTORIZACION' && (
                  <>
                    <Alert
                      message="El caso se encuentra sujeto a autorización. Una vez que sea aprobado, le estaremos notificando nuevamente."
                      type="warning"
                      banner
                    />
                    <br />
                    <br />
                  </>
                )}
              </span>
            )}

            {solicitante?.empresaJuridica && (
              <Descriptions
                title="Datos del Asegurado"
                style={{ textAlign: 'left', paddingBottom: 40 }}
                column={{ md: 2, sm: 1, xs: 1 }}
                bordered
                size="small"
              >
                <Descriptions.Item label="Razón Social" span={2}>
                  {solicitante?.empresaJuridica?.razon}
                </Descriptions.Item>
                <Descriptions.Item label="CI / RUC">
                  {solicitante?.empresaJuridica?.ruc}
                </Descriptions.Item>
                <Descriptions.Item label="Dirección" span={2}>
                  {solicitante?.empresaJuridica?.domicilio}
                </Descriptions.Item>
                <Descriptions.Item label="Correo">
                  {solicitante?.empresaJuridica?.correo}
                </Descriptions.Item>
              </Descriptions>
            )}
            {!solicitante?.empresaJuridica && (
              <DescripcionesAsegurado solicitante={solicitante} />
            )}

            {fields.tercero && (
              <Descriptions
                title="Datos del Tomador / Facturación"
                style={{ textAlign: 'left', paddingBottom: 40 }}
                bordered
                size="small"
                column={{ md: 2, sm: 1, xs: 1 }}
              >
                <Descriptions.Item label="Razón Social">
                  {fields.tercero.nombres || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="CI / RUC">
                  {fields.tercero.ruc}
                </Descriptions.Item>
                <Descriptions.Item label="Correo" span={2}>
                  {fields.tercero.correo}
                </Descriptions.Item>
                <Descriptions.Item label="Dirección" span={2}>
                  {fields.tercero.domicilio}
                </Descriptions.Item>
                <Descriptions.Item label="Celular">
                  {fields.tercero.celular}
                </Descriptions.Item>
                <Descriptions.Item label="Teléfono (Línea Baja)">
                  {fields.tercero.telefono}
                </Descriptions.Item>
                <Descriptions.Item label="Departamento">
                  {fields.tercero?.departamento?.descripcion}
                </Descriptions.Item>
                <Descriptions.Item label="Ciudad">
                  {fields.tercero?.distrito?.descripcion}
                </Descriptions.Item>
              </Descriptions>
            )}

            {solicitante?.empresaJuridica && (
              <Descriptions
                title="Datos Personales del REPRESENTANTE LEGAL"
                style={{ textAlign: 'left', paddingBottom: 40 }}
                bordered
                size="small"
                column={{ md: 2, sm: 1, xs: 1 }}
              >
                <Descriptions.Item label="Nombre y Apellido" span={2}>
                  {solicitante?.persona?.nombres}
                </Descriptions.Item>
                <Descriptions.Item label="Cédula de Identidad">
                  {solicitante?.persona?.cedula}
                </Descriptions.Item>
                <Descriptions.Item label="Domicilio">
                  {solicitante?.persona?.domicilio}
                </Descriptions.Item>
                <Descriptions.Item label="Contacto">
                  {solicitante?.persona?.celular}
                </Descriptions.Item>
                <Descriptions.Item label="Correo">
                  {solicitante?.persona?.correo}
                </Descriptions.Item>
                <Descriptions.Item label="Departamento">
                  {solicitante?.persona?.departamento?.descripcion || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Ciudad">
                  {solicitante?.persona?.distrito?.descripcion || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Nacionalidad">
                  {solicitante?.persona?.nacionalidad}
                </Descriptions.Item>
                <Descriptions.Item label="Sexo">
                  {solicitante?.persona?.sexo}
                </Descriptions.Item>
                <Descriptions.Item label="Estado civil">
                  {solicitante?.persona?.estado_civil}
                </Descriptions.Item>
                <Descriptions.Item label="Lugar nacimiento">
                  {solicitante?.persona?.lugar_nacimiento}
                </Descriptions.Item>
                <Descriptions.Item label="Fecha nacimiento">
                  {solicitante?.persona?.fecha_nacimiento &&
                    moment(
                      solicitante?.persona?.fecha_nacimiento,
                      'YYYY-MM-DD'
                    ).format('DD/MM/YYYY')}
                </Descriptions.Item>
              </Descriptions>
            )}

            <DatosProducto
              fields={fields}
              construccionData={construccionData}
            />

            <Vigencia fields={fields} />

            {fields?.json_plan?.calendarioPagos && fields?.moneda && (
              <CalendarioPagos
                lista={fields.json_plan.calendarioPagos}
                moneda={fields.moneda}
              />
            )}

            <Descriptions
              title="Compañía y Forma de pago"
              style={{ textAlign: 'left', paddingBottom: 20 }}
              bordered
              size="small"
              column={{ md: 2, sm: 1, xs: 1 }}
            >
              <Descriptions.Item label="Aseguradora">
                {fields.cotizacion?.aseguradoraEmpresa?.aseguradora
                  ?.descripcion || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Monto">
                {formatoMonedaGenerico(fields.moneda, fields.monto_total)}
              </Descriptions.Item>
              <Descriptions.Item label="Frecuencia de pago">
                {fields.modalidadPago?.descripcion}
              </Descriptions.Item>
              <Descriptions.Item label="Cuotas" span={2}>
                {fields?.json_plan?.numeroCuotas}
              </Descriptions.Item>
              <Descriptions.Item label="Medio de pago">
                {fields.metodoPago?.descripcion}
              </Descriptions.Item>
            </Descriptions>

            <div style={{ marginBottom: 20 }}>
              <DescripcionPlan
                plan={
                  getCarouselData(
                    getTable(
                      [fields?.json_plan],
                      null,
                      null,
                      fields.producto,
                      null,
                      true,
                      undefined,
                      false,
                      fields.moneda
                    )
                  )[0]
                }
              />
            </div>

            {fields.cotizacion?.aseguradoraEmpresa?.empresa?.seprelad && (
              <Descriptions
                title="Datos Laborales"
                style={{ textAlign: 'left', paddingBottom: 40 }}
                bordered
                size="small"
                column={{ md: 2, sm: 1, xs: 1 }}
              >
                <Descriptions.Item label="RUC">
                  {fields.cotizacion?.persona?.ruc}
                </Descriptions.Item>
                <Descriptions.Item label="Rango de Ingresos Mensuales Personales (Gs.)">
                  {getDescripcionesIngreso(
                    fields.cotizacion?.persona?.ingresos
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="¿Es Proveedor del Estado?">
                  {fields.cotizacion?.persona?.proveedor_estado}
                </Descriptions.Item>
                <Descriptions.Item label="Tipo empleado">
                  {fields.cotizacion?.persona?.tipo_empleado?.toUpperCase() ||
                    ''}
                </Descriptions.Item>
                <Descriptions.Item label="¿Es una Persona Expuesta Políticamente?">
                  {fields.cotizacion?.persona?.expuesta_politicamente}
                </Descriptions.Item>
                {fields.cotizacion?.persona?.expuesta_politicamente &&
                  fields.cotizacion?.persona?.expuesta_politicamente ===
                    'Si' && (
                    <>
                      <Descriptions.Item label="Cargo / Vínculo">
                        {
                          fields.cotizacion?.persona
                            ?.json_politicamente_expuesta?.vinculo
                        }
                      </Descriptions.Item>
                      <Descriptions.Item label="Entidad">
                        {
                          fields.cotizacion?.persona
                            ?.json_politicamente_expuesta?.entidad
                        }
                      </Descriptions.Item>
                      <Descriptions.Item label="Periodo">
                        {moment(
                          fields.cotizacion?.persona
                            ?.json_politicamente_expuesta?.periodo[0]
                        )?.format('DD/MM/YYYY')}{' '}
                        -{' '}
                        {moment(
                          fields.cotizacion?.persona
                            ?.json_politicamente_expuesta?.periodo[1]
                        )?.format('DD/MM/YYYY')}
                      </Descriptions.Item>
                    </>
                  )}
                {fields.cotizacion?.persona?.institucion_publica && (
                  <>
                    <Descriptions.Item label="Institución Pública">
                      {fields.cotizacion?.persona?.institucion_publica || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Cargo">
                      {fields.cotizacion?.persona?.institucion_cargo}
                    </Descriptions.Item>
                    <Descriptions.Item label="Año ingreso">
                      {fields.cotizacion?.persona?.institucion_anhoingreso}
                    </Descriptions.Item>
                  </>
                )}
                {(fields.cotizacion?.persona?.empleador_ruc !== 'A Confirmar' ||
                  fields.cotizacion?.persona?.institucion_publica) && (
                  <>
                    <Descriptions.Item label="RUC Empleador">
                      {fields.cotizacion?.persona?.empleador_ruc ||
                        'A Confirmar'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Empresa">
                      {fields.cotizacion?.persona?.empleador_empresa}
                    </Descriptions.Item>
                    <Descriptions.Item label="Teléfono">
                      {fields.cotizacion?.persona?.empleador_telefono ||
                        'A Confirmar'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Celular">
                      {fields.cotizacion?.persona?.empleador_celular ||
                        'A Confirmar'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Dirección">
                      {fields.cotizacion?.persona?.empleador_direccion}
                    </Descriptions.Item>
                  </>
                )}
              </Descriptions>
            )}
            {fields.cotizacion?.endoso && (
              <Descriptions
                title="Endoso"
                style={{ textAlign: 'left', paddingBottom: 40 }}
                bordered
                size="small"
                column={{ md: 1, sm: 1, xs: 1 }}
              >
                <Descriptions.Item label="Endoso a favor de:">
                  {fields.cotizacion?.endoso}
                </Descriptions.Item>
              </Descriptions>
            )}
            {/* PARA CASOS DIFERENTES A VEHICULO */}
            {/* TODO: falta plan */}
            {/* TODO: falta asegurados */}
            {/* TODO: falta beneficiarios */}
            {/* TODO: falta cuestionario */}
          </>
        )}
        <Modal
          bodyStyle={{ padding: 0, paddingBottom: 20 }}
          style={{ maxWidth: 1000 }}
          className="modal-header-document"
          centered
          open={dialogConfig.open}
          afterClose={() =>
            setDialogConfig({
              open: false,
              content: undefined,
              title: ''
            })
          }
          onCancel={() =>
            setDialogConfig({
              open: false,
              content: undefined,
              title: ''
            })
          }
          footer={null}
          width="90%"
        >
          {dialogConfig.content && (
            <SizeMe>
              {({ size }) => (
                <Document
                  onLoadSuccess={onDocumentLoadSuccess}
                  loading={<Spin />}
                  file={dialogConfig.content}
                >
                  <Page
                    key={`page_${currentPage + 1}`}
                    pageNumber={currentPage + 1}
                    width={size.width ? size.width : 1}
                  />
                  <div style={{ textAlign: 'center' }}>
                    <Button
                      type="file"
                      onClick={() => previousPage()}
                      disabled={currentPage === 0}
                    >
                      <LeftOutlined />
                      Anterior
                    </Button>
                    Pág. {currentPage + 1} de {numPages}
                    <Button
                      type="link"
                      onClick={() => nextPage()}
                      disabled={currentPage + 1 === numPages}
                    >
                      Siguiente
                      <RightOutlined />
                    </Button>
                  </div>
                </Document>
              )}
            </SizeMe>
          )}
        </Modal>
        <Row gutter={[16, 16]}>
          <Col md={12} xs={24}>
            <ListDocumentos
              title="Documentos"
              dataSource={[...documentos, ...formularios]}
            />
          </Col>
          <Col md={12} xs={24}>
            <Row gutter={[16, 16]}>
              {comprobantesIngreso && comprobantesIngreso.length > 0 && (
                <Col md={24} xs={24}>
                  <ListDocumentos
                    title="Comprobantes de Ingresos"
                    dataSource={comprobantesIngreso}
                  />
                </Col>
              )}
              {facturaServicios && facturaServicios.length > 0 && (
                <Col md={24} xs={24}>
                  <ListDocumentos
                    title="Facturas de Servicios"
                    dataSource={facturaServicios}
                  />
                </Col>
              )}
            </Row>
          </Col>
        </Row>

        {fileBase64 && imageVisible && (
          <Image360
            src={fileBase64}
            alt="avatar"
            style={{
              maxWidth: 140,
              height: 195,
              objectFit: 'contain'
            }}
            previewStyle={{
              visible: imageVisible,
              onVisibleChange: (value) => {
                setImageVisible(value);
              }
            }}
            hidden
          />
        )}
      </Card>
    </ResponsiveContainer>
  );
}

export default SolicitudView;
